import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, Monitor, MessageCircle, Users, MessageSquare, Database, CreditCard, Calendar, Bot, BarChart } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { useEffect } from "react";

const featureData: Record<string, any> = {
  "websites": {
    title: "High-Converting Websites",
    icon: Monitor,
    description: "We build lightning-fast platforms designed to turn visitors into paying customers. Not just pretty pages, but conversion-optimized machines.",
    benefits: [
      "Custom UI/UX Design tailored to your brand",
      "Mobile-First Development for perfect rendering on any device",
      "Conversion Rate Optimization to maximize leads",
      "Built-in Local SEO fundamentals"
    ],
    details: "Your website is the foundation of your digital presence. We utilize the power of modern frameworks to ensure your site is incredibly fast, secure, and designed with a clear user journey that guides visitors toward taking action."
  },
  "whatsapp-business": {
    title: "WhatsApp Business Integration",
    icon: MessageCircle,
    description: "Connect directly with your customers where they already spend their time. Automate conversations and provide instant support.",
    benefits: [
      "Automated welcome messages and away replies",
      "Direct integration with your CRM",
      "Broadcast promotional messages to opted-in users",
      "Seamless customer support ticketing"
    ],
    details: "By integrating WhatsApp Business into your ecosystem, you can capture leads instantly and follow up with them automatically. Reduce friction and increase your conversion rates by meeting your customers on their preferred platform."
  },
  "chat-widget": {
    title: "Intelligent Chat Widgets",
    icon: MessageSquare,
    description: "Capture leads 24/7 with a smart chat widget that answers questions, books appointments, and routes complex queries to your team.",
    benefits: [
      "24/7 lead capture and qualification",
      "Automated FAQ responses",
      "Direct calendar integration for instant booking",
      "Seamless handoff to human agents"
    ],
    details: "Stop losing visitors who have a quick question. Our chat widgets act as a 24/7 virtual assistant on your site, engaging visitors, collecting their contact information, and answering common questions instantly."
  },
  "crm-system": {
    title: "Powerful CRM System",
    icon: Database,
    description: "A centralized dashboard for all your contacts, pipelines, and communications. Never lose track of a lead again.",
    benefits: [
      "Unified inbox for Email, SMS, Web Chat, and Social Media",
      "Visual sales pipelines and opportunity tracking",
      "Automated follow-up sequences",
      "Detailed contact history and tagging"
    ],
    details: "The CRM is the brain of your operation. It consolidates all your customer interactions into one place, allowing you to track leads from their first click all the way to a closed deal, ensuring no opportunity falls through the cracks."
  },
  "payment-gateways": {
    title: "Seamless Payment Gateways",
    icon: CreditCard,
    description: "Accept payments effortlessly with secure, integrated payment gateways that support recurring billing and one-time purchases.",
    benefits: [
      "Stripe and PayPal integration",
      "Automated invoicing and receipts",
      "Subscription and recurring payment management",
      "Secure, PCI-compliant processing"
    ],
    details: "Make it as easy as possible for your customers to pay you. We integrate robust payment solutions directly into your website and CRM, allowing you to sell products, services, or subscriptions without any technical headaches."
  },
  "booking-tools": {
    title: "Automated Booking Tools",
    icon: Calendar,
    description: "Eliminate the back-and-forth of scheduling. Let clients book appointments directly on your website based on your real-time availability.",
    benefits: [
      "Two-way calendar sync (Google, Outlook, Apple)",
      "Automated SMS and email reminders",
      "Custom intake forms upon booking",
      "Time zone intelligence"
    ],
    details: "Our booking tools integrate perfectly with your CRM and calendar. Clients can easily find a time that works, and the system automatically handles the confirmations and reminders, drastically reducing no-shows."
  },
  "marketing-automation": {
    title: "Marketing Automation",
    icon: Bot,
    description: "Scale your efforts effortlessly. Set up complex workflows that nurture leads, follow up on abandoned carts, and request reviews automatically.",
    benefits: [
      "Drag-and-drop workflow builder",
      "Multi-channel campaigns (Email, SMS, Voicemail)",
      "Behavior-based triggers",
      "A/B testing and performance tracking"
    ],
    details: "Automation is the key to scaling without adding headcount. We build intelligent systems that react to customer behavior, sending the right message at exactly the right time to move them down the sales funnel."
  },
  "analytics": {
    title: "Advanced Analytics",
    icon: BarChart,
    description: "Stop guessing. Get crystal-clear visibility into your traffic, conversion rates, and campaign performance.",
    benefits: [
      "Google Analytics 4 setup and integration",
      "Custom dashboard reporting",
      "Conversion tracking and attribution",
      "ROI measurement for paid ads"
    ],
    details: "Data drives decisions. We implement robust tracking across your entire digital presence so you can see exactly where your leads are coming from, what they're doing, and which marketing efforts are generating the best return on investment."
  }
};

const FeatureDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const feature = featureData[slug || ""];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!feature) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-6">
        <h1 className="text-4xl font-bold mb-4">Feature Not Found</h1>
        <p className="text-muted-foreground mb-8">The feature you are looking for does not exist.</p>
        <Button asChild>
          <Link to="/">Return Home</Link>
        </Button>
      </div>
    );
  }

  const Icon = feature.icon;

  return (
    <div className="min-h-screen bg-background pt-32 pb-24">
      <SEOHead
        title={`${feature.title} - LRH Solutions`}
        description={feature.description}
        canonical={`/feature/${slug}`}
      />
      <div className="container mx-auto px-6 max-w-4xl">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-12">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>
        
        <div className="flex flex-col md:flex-row gap-8 items-start mb-16">
          <div className="w-24 h-24 rounded-3xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0 shadow-[0_0_30px_hsl(var(--primary)/0.15)]">
            <Icon className="w-12 h-12 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-4">
              {feature.title}
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              {feature.description}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div className="space-y-8">
            <h2 className="text-2xl font-bold">How It Works</h2>
            <p className="text-muted-foreground leading-relaxed">
              {feature.details}
            </p>
            <Button asChild size="lg" className="rounded-full font-bold shadow-[0_0_20px_hsl(var(--primary)/0.3)]">
              <Link to="/booking">Implement This Feature</Link>
            </Button>
          </div>
          
          <div className="bg-card/50 border border-border/50 rounded-3xl p-8 backdrop-blur-sm">
            <h3 className="text-xl font-bold mb-6">Key Benefits</h3>
            <ul className="space-y-4">
              {feature.benefits.map((benefit: string, index: number) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                  <span className="font-medium">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureDetail;
